<template>
  <PbPopup :scrollToTop="false" fitParentWidth>
    <template #action="{ toggle }">
      <div class="drop-down-input-field" @click="handleOpen(toggle)">
        <div class="drop-down-input-field__value-wrapper">
          <div v-if="props.label" class="drop-down-input-field__label">
            {{ props.label }}
          </div>
          <div class="drop-down-input-field__value">
            <slot name="value" />
          </div>
        </div>
        <div class="drop-down-input-field__toggle">
          <div class="drop-down-input-field__side">
            <slot name="input-side" />
          </div>
          <img src="@kanoAssets/images/icons/chevron-down.svg?url" width="16" height="16" alt="chevron-down" />
        </div>
      </div>
    </template>
    <template #default="{ hide }">
      <div class="drop-down-header">
        <div class="drop-down-header__side"></div>
        <div v-if="props.dropdownTitle" class="drop-down-header__title">
          {{ props.dropdownTitle }}
        </div>
        <div class="drop-down-header__side drop-down-header__close">
          <img
            src="@kanoAssets/images/icons/circle-close.svg?url"
            width="24"
            height="24"
            alt="close"
            @click="hide"
          />
        </div>
      </div>
      <div v-if="props.canFilter" class="drop-down-search">
        <input
          ref="search"
          type="text"
          :placeholder="$t('calculator.searchPlaceholder')"
          class="drop-down-search__input"
          :value="searchString"
          @input="filter"
        />
        <div v-show="searchString.length > 0"
           class="drop-down-search__clear"
           @click="clearFilter"
        >
          <img src="@kanoAssets/images/icons/close.svg?url" width="16" height="16" alt="clear-search" />
        </div>
      </div>
      <PbScroll>
        <div class="drop-down-list">
          <slot name="default" :hide="hide" />
        </div>
      </PbScroll>
    </template>
  </PbPopup>
</template>

<script setup lang="ts">
import PbPopup from "@kano/components/ExchangeFormV2/PbPopup.vue";
import PbScroll from "@kano/components/ExchangeFormV2/PbScroll.vue";
import { ref } from "vue";

const props = withDefaults(defineProps<{
  value: string,
  label: string|null,
  dropdownTitle: string|null,
  canFilter: boolean,
}>(), {
  canFilter: false,
});

const searchString = ref<string>('');

const emit = defineEmits<{
  (event: 'filter'): void,
  (event: 'open'): void,
}>();

const filter = (event) => {
  const filterString = event.target.value;

  searchString.value = filterString;
  emit('filter', filterString);
}

const clearFilter = () => {
  searchString.value = '';
  emit('filter', '');
}

const handleOpen = (callback) => {
  emit('open');
  callback();
}
</script>

<style scoped lang="scss">
@use '@kanoAssets/styles/breakpoints.scss';

.drop-down-input-field {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid var(--color-base-16);
  border-radius: 12px;

  transition: border-color 150ms ease;

  &:focus-within, &:hover {
    border-color: #5F70DB;
  }

  &__value-wrapper {
    flex-grow: 1;
  }

  &__label {
    font-size: 14px;
    color: var(--color-base-64);
    line-height: 20px;
  }

  &__value {
    font-family: Inter Tight, Inter Tight fallback, sans-serif;
    font-weight: 600;
  }

  &__toggle {
    display: flex;
    gap: 12px;
    align-items: center;
    cursor: pointer;
  }

  &__side {
    font-family: Inter Tight, Inter Tight fallback, sans-serif;
    font-weight: bold;
    display: flex;
  }
}

.drop-down-header {
  display: flex;
  align-items: center;
  padding: 0 24px;
  min-height: 30px;
  margin-bottom: 32px;
  justify-content: stretch;
  flex: 0 0 auto;

  @include breakpoints.desktop {
    margin-bottom: 22px;
  }

  &__side {
    flex: 1 1 0;
    display: flex;
    align-items: center;
  }

  &__title {
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
  }

  &__close {
    justify-content: end;
  }

  &__close img {
    cursor: pointer;

    @include breakpoints.desktop {
      display: none;
    }
  }
}

.drop-down-search {
  margin-bottom: 34px;
  position: relative;

  @include breakpoints.desktop {
    margin-bottom: 20px;
  }

  &__input {
    font-size: 16px;
    background: var(--color-base-0);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding-left: 24px;
    padding-right: 24px;
    width: 100%;
    outline: none;
    height: 36px;
    transition: border-color 0.2s ease-in-out;

    ::placeholder {
      font-size: 16px;
      line-height: 19px;
      color: var(--color-base-48);
    }

    &:focus {
      border-color: var(--color-primary);
    }
  }

  &__clear {
    position: absolute;
    right: 28px;
    top: 10px;
    cursor: pointer;
  }
}

.drop-down-list {
  margin: 0 14px;
  max-height: 385px;
}
</style>